import { useClientPortalConfigContext } from "../context";
import { alertClasses, checkboxClasses, ThemeOptions } from "@mui/material";
import { useMemo } from "react";
import { default as componentsLibraryThemeObject, palette } from "@powerdiary/components-library/lib/theme";
import { isValidHexColor } from "@powerdiary/components-library/lib";

interface IClientPortalTheme {
    clientPortalThemeOptions: Partial<ThemeOptions>;
}

export function useClientPortalTheme(): IClientPortalTheme {
    const { clientPortalConfig, isLoading } = useClientPortalConfigContext();
    const defaultColor = palette.dusk["500"];

    const brandColor = useMemo(() => {
        const userSelectedColor = clientPortalConfig?.clientPortalBrandColor;
        if (userSelectedColor && isValidHexColor(userSelectedColor)) {
            return userSelectedColor;
        }
        return defaultColor;
    }, [clientPortalConfig, defaultColor]);

    const clientPortalThemeOptions: Partial<ThemeOptions> = useMemo(() => {
        if (isLoading) {
            return undefined;
        }

        return {
            palette: {
                background: {
                    default: palette.neutral["100"],
                    paper: "#fff",
                },
                primary: {
                    main: brandColor,
                },
                text: {
                    primary: palette.neutral["900"],
                },
            } as ThemeOptions["palette"],
            breakpoints: componentsLibraryThemeObject.breakpoints,
            typography: { fontFamily: '"Open Sans", sans-serif', fontSize: 16 },
            components: {
                MuiButton: {
                    variants: [
                        ...componentsLibraryThemeObject.components.MuiButton.variants,
                        {
                            props: { variant: "text" },
                            style: {
                                textTransform: "none",
                            },
                        },
                        ...componentsLibraryThemeObject.components.MuiButton.variants,
                    ],
                    defaultProps: componentsLibraryThemeObject.components.MuiButton.defaultProps,
                },
                MuiDialog: componentsLibraryThemeObject.components.MuiDialog,
                MuiLink: componentsLibraryThemeObject.components.MuiLink,
                MuiTypography: componentsLibraryThemeObject.components.MuiTypography,
                MuiSwitch: componentsLibraryThemeObject.components.MuiSwitch,
                MuiButtonBase: componentsLibraryThemeObject.components.MuiButtonBase,
                MuiMenuItem: componentsLibraryThemeObject.components.MuiMenuItem,
                MuiTooltip: componentsLibraryThemeObject.components.MuiTooltip,
                MuiChip: componentsLibraryThemeObject.components.MuiChip,
                MuiCheckbox: {
                    styleOverrides: {
                        root: {
                            color: palette.neutral["500"],
                            height: "18px",
                            width: "18px",
                            margin: "8px",
                            [`&:not(.${checkboxClasses.disabled})`]: {
                                "&:hover": {
                                    color: brandColor,
                                },
                                [`&.${checkboxClasses.checked}`]: {
                                    color: brandColor,
                                },
                            },
                        },
                    },
                    variants: [...componentsLibraryThemeObject.components.MuiCheckbox.variants],
                },
                MuiFormControlLabel: componentsLibraryThemeObject.components.MuiFormControlLabel,
                MuiFormLabel: componentsLibraryThemeObject.components.MuiFormLabel,
                MuiInputLabel: componentsLibraryThemeObject.components.MuiInputLabel,
                MuiInputBase: componentsLibraryThemeObject.components.MuiInputBase,
                MuiAlert: {
                    ...componentsLibraryThemeObject.components.MuiAlert,
                    variants: [
                        ...componentsLibraryThemeObject.components.MuiAlert.variants,
                        {
                            props: { color: "userSelectedColor" },
                            style: {
                                backgroundColor: palette.neutral["white"],
                                border: `1px solid ${brandColor}`,
                                color: brandColor,
                                [`& .${alertClasses.icon}`]: {
                                    color: brandColor,
                                },
                            },
                        },
                    ],
                },
                MuiStepper: componentsLibraryThemeObject.components.MuiStepper,
                MuiCard: componentsLibraryThemeObject.components.MuiCard,
                MuiTextField: componentsLibraryThemeObject.components.MuiTextField,
                MuiSelect: componentsLibraryThemeObject.components.MuiSelect,
                MuiAutocomplete: componentsLibraryThemeObject.components.MuiAutocomplete,
                MuiToggleButtonGroup: componentsLibraryThemeObject.components.MuiToggleButtonGroup,
            },
        } as ThemeOptions;
    }, [isLoading, brandColor]);

    return { clientPortalThemeOptions };
}
